/**
 * HC Dashboard - Main Application Component
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme, App as AntApp, message } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import MainLayout from './components/Layout/MainLayout';
import Dashboard from './pages/Dashboard';
import Agents from './pages/Agents';
import AuditsList from './pages/AuditsList';
import AuditDetail from './pages/AuditDetail';
import Policies from './pages/Policies';
import Observations from './pages/Observations';
import Login from './pages/Login';
import LoadingSpinner from './components/LoadingSpinner';
import { healthApi } from './api/hcApi';
import type { HealthStatus } from './types';
import './App.css';

// Initialize dayjs plugins
dayjs.extend(relativeTime);

// Auth token storage key
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'hc_user';

const App: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [systemStatus, setSystemStatus] = useState<HealthStatus>('unknown');

  // Check for existing auth on mount
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      setAuthenticated(true);
    }
    setLoading(false);
  }, []);

  // Poll system health when authenticated
  const fetchHealth = useCallback(async () => {
    if (!authenticated) return;
    try {
      const response = await healthApi.getHealth();
      if (response.success) {
        setSystemStatus((response.data?.status as HealthStatus) || 'healthy');
      }
    } catch (err) {
      // Auth may have expired
      console.warn('Health check failed:', err);
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) {
      fetchHealth();
      const interval = setInterval(fetchHealth, 30000);
      return () => clearInterval(interval);
    }
  }, [authenticated, fetchHealth]);

  const handleLoginSuccess = (token: string, user: { email: string; role: string }) => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    setAuthenticated(true);
    message.success(`Welcome, ${user.email}`);
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    setAuthenticated(false);
    setSystemStatus('unknown');
    message.info('Logged out successfully');
  };

  if (loading) {
    return <LoadingSpinner fullscreen tip="Loading HC Dashboard..." />;
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
          colorBgContainer: '#ffffff',
        },
        components: {
          Layout: {
            siderBg: '#001529',
            headerBg: '#ffffff',
          },
          Menu: {
            darkItemBg: '#001529',
            darkSubMenuItemBg: '#000c17',
          },
        },
      }}
    >
      <AntApp>
        <BrowserRouter basename="/hc">
          {authenticated ? (
            <Routes>
              <Route
                path="/"
                element={
                  <MainLayout
                    systemStatus={systemStatus}
                    onLogout={handleLogout}
                  />
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="agents" element={<Agents />} />
                <Route path="audits" element={<AuditsList />} />
                <Route path="audits/:id" element={<AuditDetail />} />
                <Route path="observations" element={<Observations />} />
                <Route path="policies" element={<Policies />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          ) : (
            <Routes>
              <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
            </Routes>
          )}
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
